/**
 * BI components brand tokens — branded with the Omnitok palette.
 *
 * Source of truth for the colours charts, KPIs, tables and filters
 * paint as SVG attributes or inline styles (series palette, heat ramps,
 * chart chrome, state tones). UI classes come from the @omnitok/ui
 * Tailwind preset; `primary` and `accent` below only mirror it.
 */
export const brand = {
  // Quiet quantitative ramp for the HTML reference. Intensity is magnitude,
  // never a good/bad judgement. Explicit opt-in through colorScale props.
  referenceHeat: ['#FAF9FD', '#F2EFF9', '#EAE5F5', '#E1D9F0', '#D5CAE9', '#C4B7E1', '#B1A1D5', '#9784C3'],
  // Look v3 Fase 1 (C3, 2026-10-26): `primary` and `accent` MIRROR the kit
  // (@omnitok/ui 3.2.1 tailwind preset), same names, same values. They are
  // here only because an SVG attribute needs a concrete string; Tailwind
  // classes resolve through the kit preset (`primary-soft`, `primary-ink`,
  // `accent-dark`, ...). The old numeric 50-900 ladders are gone: a class
  // like `bg-primary-50` no longer exists.
  primary: {
    DEFAULT: '#4D4A9D',
    dark: '#3d3a8c',
    light: '#6b68b3',
    soft: '#F0EFF8',
    ink: '#3B3880',
    line: '#E0DEF2',
  },
  accent: {
    DEFAULT: '#E0156D',
    dark: '#C8105F',
    light: '#FF177B',
  },
  // Quantitative heat ramp (light to dark magenta) for maps, calendar and
  // heatmap cells. It is the old accent 50/200/400/500/700/900 ladder, kept
  // as a DATA ramp (magnitude, never a judgement), not as a UI token.
  heatRamp: ['#FDEDF5', '#F6B1D3', '#EC509D', '#E0156D', '#9C1155', '#400723'],
  // Series palette (DQ3, Pablo 2026-10-25): ONE list, violet first as the
  // kit says, then the action pink, then six hues chosen with the dataviz
  // validator (Machado 2009 simulation, OKLab deltaE x100) so that, on a
  // white card:
  //   - every slot is >= 3:1 (marks),
  //   - adjacent slots stay >= 8 apart under protanopia and deuteranopia
  //     (the product owner is red-green colour-blind) and >= 15 apart
  //     under normal vision,
  //   - the first three are also apart pairwise (scatter, maps).
  // Measured numbers live in the PR that set it (Fase 1 of the look v3 QA).
  // Assign in this order. A chart with more than 8 series still cycles
  // (`i % palette.length`); folding the rest into "Other" is not built.
  palette: [
    '#4D4A9D', // S1 violet (kit primary)
    '#E0156D', // S2 action pink (kit accent)
    '#B26A00', // S3 amber
    '#2A78D6', // S4 blue
    '#00876A', // S5 teal
    '#C2501F', // S6 orange
    '#8E3C97', // S7 purple
    '#6E7F00', // S8 olive
  ],
  // Neutral chart chrome (redesign v2): axis text, muted captions,
  // pill tracks, card borders, grid lines, zero axis.
  //
  // Contrast sweep 2026-07-23 (Pablo, red-green colour-blind: "harto
  // texto en un gris más tenue… no alcanzo a ver"): the two TEXT
  // tokens stepped one rung darker on the same blue-grey ladder —
  // `faint` #9AA0B5 → #7A8099 (the old `muted`), `muted` #7A8099 →
  // #5A607D. LINE/FILL tokens (track/border/grid/axis) are untouched;
  // chrome lines at higher contrast would compete with the data ink.
  //
  // Sweep completion 2026-07-24 (motion audit): the HTML side of the
  // sweep landed, but the SVG side did not — `faint` feeds every axis
  // TICK LABEL in charts.jsx / charts-interactive.jsx at fontSize 9-10,
  // and #7A8099 on a white card measures **3.91:1**, under the 4.5:1
  // WCAG AA floor for small text. Stepped to #6A708B, the exact HALF
  // rung between `faint` and `muted` on the same arithmetic ladder
  // (the sweep's rungs are a uniform −32/−32/−28 per step; this is
  // −16/−16/−14), which keeps the faint↔muted hierarchy visible while
  // clearing AA.
  //
  // Measured (WCAG 2.1 relative luminance, on the real card bg #FFF):
  //   #9AA0B5 pre-sweep 2.60:1   #7A8099 was 3.91:1   #6A708B 4.88:1 ✓
  // Under the RefreshOverlay's 0.75 dim, #6A708B lands at 3.02:1.
  // That is BELOW small-text AA on purpose: during a refetch the
  // content is deliberately attenuated as the "this is updating"
  // signal, and 0.75 is a value the product owner calibrated by hand —
  // it is not a target to optimise. Worth knowing: 3.02:1 does clear
  // the 3:1 floor WCAG 1.4.11 sets for graphical objects, and it beats
  // the 2.61:1 the dimmed state used to sit at.
  neutral: {
    text:   '#1E2235', // primary chart text / net line
    muted:  '#5A607D', // secondary text (legend names, subtitles) — 6.17:1
    faint:  '#6A708B', // SVG axis tick labels — 4.88:1 on white (AA)
    track:  '#F2F3F8', // pill-bar track / chip background
    border: '#E7E9F2', // card / chip borders
    grid:   '#F0F1F7', // horizontal grid lines
    axis:   '#D6DAE6', // zero baseline on diverging charts
  },
  // State colours. The delta chips (up/down) and the threshold bands share
  // the colour-blind pair below (Look v3 Fase 1, C5): the old delta
  // green #059669 / red #EF4444 pair is gone, it is exactly the pair a
  // deuteranope cannot separate.
  semantic: {
    // Look v3: the secondary grey that holds 4.5:1 on the chip wash.
    neutralText:  '#5B6478',
    neutralBg:    '#F2F3F8',
    // ── Threshold band tones (epic v3 scorecards) ──
    // A delta is three-state because it has a sign. A THRESHOLD has a
    // caution band, so the tone vocabulary
    // needs a fourth colour — see `resolveTone` in lib/shared.jsx for
    // why the two vocabularies stay separate instead of merging.
    warning:      '#D97706',
    warningText:  '#B45309',
    warningBg:    '#FFFBEB',
    // Per-tone borders. The pastel `*Bg`/`*Text` pairs above never
    // needed one (a delta chip is borderless), but every tinted
    // scorecard surface — status chips, callouts, tone table cells —
    // reads as a floating smudge without it.
    warningBorder:  '#FDE68A',
    neutralBorder:  '#E7E9F2',
    // ── Colour-blind-friendly BAND colours (2026-07-21) ──
    // Pablo (the product owner) is red-green colour-blind. The classic
    // green #059669 / red #EF4444 threshold pair is exactly the pair a
    // deuteranope struggles to separate, so the SCORECARD band
    // vocabulary (toneStyle in lib/shared.jsx) shifts to a bluish-green
    // (teal — its blue component survives deuteranopia) vs a DEEP, less
    // neon red that differs in LIGHTNESS as well as hue. Vermilion was
    // rejected: it collides with the amber warn band for everyone.
    // Colour is never the only channel — ToneIcon (✓/!/×) carries the
    // state by SHAPE (lib/scorecard.jsx). Delta chips use the same pair
    // and add the ▲/▼ sign.
    bandPositive:       '#009E73',
    bandPositiveText:   '#0F766E',
    bandPositiveBg:     '#F0FDFA',
    bandPositiveBorder: '#99F6E4',
    bandNegative:       '#B42318',
    bandNegativeText:   '#912018',
    bandNegativeBg:     '#FEF3F2',
    bandNegativeBorder: '#FECDCA',
    // Look v3 (2026-10-24): the quiet surface of a Callout per tone. The
    // tone itself lives in the icon and the title (shape and word); the
    // surface only hints it, behind one uniform border.
    surfaceGood:    '#F8FCFA',
    surfaceWarn:    '#FEFBF5',
    surfaceBad:     '#FFFAFC',
    surfaceNeutral: '#FBFAFE',
    surfaceInfo:    '#F7F5FC',
    // Look v3 (DQ2, 2026-10-25): the three tones a config may name for a
    // state that is neither good, bad nor a warning. Kit v3 values
    // (@omnitok/ui preset: primary, primary.ink/soft/line, info.light,
    // neutral-400), so the info tones read as the kit's info.
    info:       '#4D4A9D',
    infoLight:  '#B6B8DF',
    infoText:   '#3B3880',
    infoBg:     '#F0EFF8',
    infoBorder: '#E0DEF2',
    muted:      '#98A1B3',
  },
  sidebar: { from: '#211f4b', to: '#3d3a8c' },
  // Look v3 (2026-10-24): the chrome navy of the approved reference, the
  // same value as the kit's --omni-navy. Fills the strong selection of a
  // segmented control (the report's primary axes).
  ink: '#1A1240',
};

// ── CSS custom properties: the indirection layer (Plan C fase 3.1) ────
//
// Tailwind compiles a class to a literal hex at BUILD time. A colour a
// tenant picks at 18:00 can never become a class in a bundle built last
// Tuesday. The escape is that the CLASS stays fixed while its VALUE
// becomes indirect: the preset emits ``rgb(var(--bi-accent) /
// <alpha-value>)`` and ships the kit's hex as the ``:root`` default, so an
// UNTHEMED tenant renders the kit exactly and a theme
// (``tenancy.theming.theme_css``) is only ever an override.
//
// Since Look v3 Fase 1 only the tenant-editable tokens go through a
// variable: the accent (``bg-accent``, ``bg-accent-dark``), the primary
// used by the two brand gradients, and the sidebar gradient.
// ``--bi-accent-600`` holds ``accent.dark`` because 600 is the rung
// ``tenancy.theming.derive_scale`` writes for a tenant's darker accent.
//
// CHANNELS ARE MANDATORY, NOT A STYLE PREFERENCE. ~80 sites in this repo
// use Tailwind's opacity modifier on these two scales (``ring-accent/30``
// ×8 and ``border-accent/40`` ×7 in ``lib/tables.jsx`` alone), and the
// modifier can only substitute an alpha into a colour written as
// ``rgb(<channels> / <alpha-value>)``. A variable holding ``#4D4A9D``
// would make every one of those classes render at full opacity.

/** ``'#4D4A9D'`` → ``'77 74 157'`` — the channel triplet a CSS var holds. */
export function hexToChannels(hex) {
  const text = String(hex).trim();
  // The leading `#` is REQUIRED, matching `tenancy.theming.parse_hex` on
  // the Python side. The two implementations feed the same variables —
  // one compiles the defaults, the other writes a tenant's override — so
  // they must accept exactly the same inputs or a value that is valid in
  // one place becomes a silent failure in the other.
  if (!text.startsWith('#')) {
    throw new Error(`hexToChannels: ${JSON.stringify(hex)} is missing its leading '#'`);
  }
  const body = text.slice(1);
  const full =
    body.length === 3
      ? body
          .split('')
          .map((c) => c + c)
          .join('')
      : body;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    throw new Error(
      `hexToChannels: ${JSON.stringify(hex)} is not a 3- or 6-digit hex colour`,
    );
  }
  const n = parseInt(full, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

/** The ``--bi-*`` variable name for one token, e.g. ``--bi-primary-500``. */
export function brandVarName(role, step) {
  return step === undefined ? `--bi-${role}` : `--bi-${role}-${step}`;
}

/**
 * Every ``--bi-*`` custom property and its DEFAULT channel triplet.
 *
 * This is what the preset writes into the compiled ``:root``, derived from
 * the `brand` object above, so there is no second list of hexes to drift.
 *
 * What is DELIBERATELY absent: `semantic` (including the colour-blind
 * `band*` pair and the warning amber), `neutral` (chart chrome) and the
 * series palette after the accent slot. Those are not editable by design,
 * see `packages/tenancy/src/tenancy/theming.py`.
 */
export function brandCssVars() {
  return {
    [brandVarName('primary')]: hexToChannels(brand.primary.DEFAULT),
    // The kit's `primary` family (2026-10-29): selected options, links,
    // active borders. A tenant's `brand_color` re-declares all six
    // (`tenancy.theming.shell_tokens`).
    [brandVarName('primary', 'dark')]: hexToChannels(brand.primary.dark),
    [brandVarName('primary', 'light')]: hexToChannels(brand.primary.light),
    [brandVarName('primary', 'soft')]: hexToChannels(brand.primary.soft),
    [brandVarName('primary', 'ink')]: hexToChannels(brand.primary.ink),
    [brandVarName('primary', 'line')]: hexToChannels(brand.primary.line),
    [brandVarName('accent')]: hexToChannels(brand.accent.DEFAULT),
    [brandVarName('accent', 600)]: hexToChannels(brand.accent.dark),
    [brandVarName('sidebar', 'from')]: hexToChannels(brand.sidebar.from),
    [brandVarName('sidebar', 'to')]: hexToChannels(brand.sidebar.to),
    // The chrome navy (`bg-navy`, the strong selection of a segmented
    // control). A tenant's sidebar gradient re-declares it.
    [brandVarName('navy')]: hexToChannels(brand.ink),
    // The Home hero. Not colours of their own: until a tenant sets a banner
    // gradient the hero is the brand gradient (primary → accent), so these
    // two are references, and a tenant that only sets `brand_color` still
    // gets a hero in its colour.
    [brandVarName('banner', 'from')]: `var(${brandVarName('primary')})`,
    [brandVarName('banner', 'to')]: `var(${brandVarName('accent')})`,
  };
}
